/**
 * mobile-cro.js — mobile conversion-rate behaviour fixes.
 *
 * Two independent features, each a no-op when its markup is absent:
 *
 *   1. Collapse the filter drawer's groups on mobile. The theme renders
 *      every <details> with `open` when settings.expand_filters is true
 *      (facet-filters.liquid:111/187/337), which on mobile unrolls all
 *      ~16 filter groups at once — a shopper who wants "Marca" scrolls
 *      past every other expanded group first. Desktop keeps them open.
 *
 *   2. Show a sticky checkout bar on the cart page while the real
 *      checkout button is off-screen (see mobile-cro.css §4).
 *
 *   4. Publish the cookie banner state (window.raceitConsent, the
 *      `rc:consent-change` event and --rc-consent-h) so floating UI —
 *      the product dock and the newsletter — never fights it for space.
 *
 * Paired stylesheet: assets/mobile-cro.css
 */
(() => {
  const MOBILE = '(max-width: 768.98px)';

  /* ── 1. Filter drawer: collapse groups on mobile ────────────────────
     Runs while the drawer is still translated off-screen
     (facet-filters.css:7), so the collapse is never visible as a flash.

     #active-filters is deliberately left alone: it is short, it is
     hidden entirely when nothing is applied, and it is the one panel
     whose whole job is to show current state.
  ────────────────────────────────────────────────────────────────────── */
  const syncFilterDisclosures = () => {
    const form = document.getElementById('facets');
    if (!form) return;

    const isMobile = window.matchMedia(MOBILE).matches;

    // #active-filters carries no `.filter` class (facet-filters.liquid:111),
    // so it is excluded here and stays open — it is short, it is hidden
    // when nothing is applied, and showing current state is its whole job.
    form.querySelectorAll('details.filter.disclosure').forEach((details) => {
      if (isMobile) {
        // A group the shopper has already filtered on stays open, so their
        // selections remain visible in context rather than disappearing
        // behind a collapsed header.
        if (details.dataset.type !== 'sort' && details.querySelector('input:checked')) return;

        // Only touch panels that arrived open, and remember that we did,
        // so a resize back to desktop can restore the authored state.
        if (details.open) {
          details.dataset.mobileCollapsed = 'true';
          details.open = false;
        }
      } else if (details.dataset.mobileCollapsed === 'true') {
        delete details.dataset.mobileCollapsed;
        details.open = true;
      }
    });
  };

  syncFilterDisclosures();

  // The facet form is replaced wholesale by the Section Rendering API on
  // every filter change (facet-filters.js:248), which restores the
  // server-rendered `open` attributes — so re-apply on its broadcast.
  document.addEventListener('on:facet-filters:updated', syncFilterDisclosures);
  window.matchMedia(MOBILE).addEventListener('change', syncFilterDisclosures);

  /* ── 2. Cart page: sticky checkout bar ──────────────────────────────── */
  const stickyBar = document.querySelector('.cart-sticky-checkout');
  const cartForm = document.getElementById('cart');

  if (stickyBar && cartForm && 'IntersectionObserver' in window) {
    const cartMobile = window.matchMedia('(max-width: 849.98px)');
    let realCheckoutBtn = null;
    let realCheckoutVisible = true;

    const syncStickyVisibility = () => {
      const hasItems = Boolean(cartForm.querySelector('cart-items .cart-item:not([hidden]):not([data-recargo-fee="true"])'));
      const visible = cartMobile.matches && hasItems && realCheckoutBtn && !realCheckoutVisible;
      if (!visible && stickyBar.contains(document.activeElement)) {
        const target = realCheckoutBtn || cartForm.querySelector('.js-cart-empty a');
        if (target) target.focus({ preventScroll: true });
      }
      stickyBar.classList.toggle('is-visible', Boolean(visible));
      stickyBar.hidden = !visible;
      stickyBar.inert = !visible;
      stickyBar.setAttribute('aria-hidden', String(!visible));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((item) => item.target === realCheckoutBtn);
        if (!entry) return;
        realCheckoutVisible = entry.isIntersecting;
        syncStickyVisibility();
      },
      { rootMargin: '0px 0px -80px 0px' }
    );

    const syncCartCheckout = () => {
      const nextButton = cartForm.querySelector('#cart-summary [name="checkout"]');
      if (nextButton !== realCheckoutBtn) {
        observer.disconnect();
        realCheckoutBtn = nextButton;
        realCheckoutVisible = true;
        if (realCheckoutBtn) observer.observe(realCheckoutBtn);
      }
      syncStickyVisibility();
    };

    // Cart sections replace the summary button after every edit. Observe the
    // current button and keep an empty or hidden sticky bar out of keyboard focus.
    new MutationObserver(syncCartCheckout).observe(cartForm, { childList: true, subtree: true });
    cartMobile.addEventListener('change', syncCartCheckout);
    cartForm.addEventListener('submit', (event) => {
      if (cartForm.hasAttribute('data-cart-updating')) event.preventDefault();
    }, true);
    syncCartCheckout();
  }

  /* ── 3. Browse pages: floating back-to-top ──────────────────────────── */
  const toTop = document.querySelector('.mobile-to-top');

  if (toTop) {
    toTop.hidden = false;

    let ticking = false;
    const update = () => {
      // Two viewport heights in — far enough that the toolbar is well out
      // of reach, but before the shopper has given up scrolling.
      toTop.classList.toggle('is-visible', window.scrollY > window.innerHeight * 2);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    toTop.addEventListener('click', () => {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });

    update();
  }

  /* ── 4. Cookie banner state ─────────────────────────────────────────────
     Shopify injects #shopify-pc__banner a moment after load and hides it
     once the shopper answers. Everything else that floats reads this
     instead of guessing: the dock sits above the banner (--rc-consent-h)
     and the newsletter waits until it has gone (rc:consent-change).
  ────────────────────────────────────────────────────────────────────── */
  const root = document.documentElement;
  let consentBanner = null;
  let consentOpen = false;

  const syncConsent = () => {
    const banner = consentBanner || document.getElementById('shopify-pc__banner');
    if (banner && banner !== consentBanner) {
      consentBanner = banner;
      new MutationObserver(syncConsent).observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      if ('ResizeObserver' in window) new ResizeObserver(syncConsent).observe(banner);
      if (bodyWatch) bodyWatch.disconnect();
    }

    const rect = banner ? banner.getBoundingClientRect() : null;
    const open = Boolean(banner && rect.height > 0 && window.getComputedStyle(banner).display !== 'none');
    root.style.setProperty('--rc-consent-h', open ? `${Math.round(window.innerHeight - rect.top)}px` : '0px');
    if (open === consentOpen) return;
    consentOpen = open;
    root.classList.toggle('rc-consent-open', open);
    document.dispatchEvent(new CustomEvent('rc:consent-change', { detail: { open } }));
  };

  window.raceitConsent = { isOpen: () => consentOpen };

  let bodyWatch = null;
  if (!document.getElementById('shopify-pc__banner')) {
    let queued = false;
    bodyWatch = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        if (document.getElementById('shopify-pc__banner')) syncConsent();
      });
    });
    bodyWatch.observe(document.body, { childList: true, subtree: true });
    // The banner either appears early or not at all (consent already given,
    // or not required in this region). Stop watching the whole page after that.
    window.setTimeout(() => bodyWatch && bodyWatch.disconnect(), 15000);
  }
  document.addEventListener('visitorConsentCollected', () => window.setTimeout(syncConsent, 50));
  syncConsent();
})();
